import {
  API_ROUTES,
  deleteCall,
  getCall,
  getCallBlob,
  postFormDataCall,
} from 'utils/api';
import { getModifiedApiError } from 'utils/services/common.service';
import { UploadKeys } from '../types';
import { DownloadJobFilePayload } from './types';

export const uploadJobFilesApi = async (
  files: Array<any>,
  jobId: number,
  folderKey: UploadKeys,
) => {
  try {
    const fd = new FormData();
    files.map(file => {
      fd.append('files', file);
    });

    const { data } = await postFormDataCall(
      `${API_ROUTES.jobFileUpload}/${folderKey}/${jobId}`,
      fd,
    );
    return data;
  } catch (error) {
    throw new Error(getModifiedApiError(error));
  }
};

export const getJobFolderDetailsApi = async payload => {
  try {
    const { data } = await getCall(
      `${API_ROUTES.job}/${payload.jobId}/files?isHistory=${
        payload.isHistory ?? false
      }`,
    );
    return data;
  } catch (error) {
    throw new Error(getModifiedApiError(error));
  }
};

/**
 * Download requests are sent with responseType 'blob', so when the server replies with
 * an error the JSON body arrives as a Blob rather than a parsed object. The usual error
 * extraction reads `data.message`, finds nothing on a Blob, and the reason for the
 * failure is lost. Read the Blob back to text and parse it so the real message (e.g.
 * "This file is no longer available in storage.") reaches the user.
 */
const withParsedBlobError = async (error: unknown) => {
  const data = (error as { data?: unknown })?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      return { ...(error as object), data: JSON.parse(await data.text()) };
    } catch {
      // Body was not JSON - fall through and let the generic message apply.
    }
  }
  return error;
};

export const downloadJobFile = async (payload: DownloadJobFilePayload) => {
  try {
    const { path, jobId, fileId, isHistory } = payload;
    const { data } = await getCallBlob(
      `${API_ROUTES.job}/download/${path}/${jobId}/${fileId}?isHistory=${
        isHistory ?? false
      }`,
    );
    return data;
  } catch (error) {
    throw new Error(getModifiedApiError(await withParsedBlobError(error)));
  }
};

/**
 * Permanently delete files from a job folder. Super Admin only (enforced server-side).
 * A single-row delete sends a list of one, so both UI paths behave identically.
 *
 * Resolves with { deleted, failed } so the caller can report partial results - files are
 * deleted one at a time server-side and some can fail while others succeed.
 */
export const deleteJobFilesApi = async ({ path, jobId, fileIds }) => {
  try {
    const { data } = await deleteCall(
      `${API_ROUTES.job}/files/${path}/${jobId}`,
      { fileIds },
    );
    return data;
  } catch (error: any) {
    throw new Error(getModifiedApiError(error));
  }
};

export const getSingedUrlJobFile = async payload => {
  try {
    const { path, jobId, fileId, isHistory } = payload;
    const { data } = await getCall(
      `${API_ROUTES.job}/generateLink/${path}/${jobId}/${fileId}?isHistory=${
        isHistory ?? false
      }`,
    );
    return data;
  } catch (error) {
    throw new Error(getModifiedApiError(error));
  }
};
